import React from 'react'
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { WarningAmber as ConflictIcon } from '@mui/icons-material'
import { SyncConflict } from '@/lib/db/indexed-db'

interface Props {
  open: boolean
  conflicts: SyncConflict[]
  onResolve: (id: string) => void
  onClose: () => void
}

const operationLabel = (op: string) => {
  if (op === 'CREATE') return 'Create'
  if (op === 'UPDATE') return 'Update'
  if (op === 'DELETE') return 'Delete'
  return op
}

const SyncConflictDialog: React.FC<Props> = ({ open, conflicts, onResolve, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ConflictIcon color="warning" />
        <span>Sync Conflicts ({conflicts.length})</span>
      </Stack>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        The following changes could not be applied because the server version has changed. Choose
        how to resolve each conflict. Selecting <strong>Keep server version</strong> discards your
        local change.
      </Typography>
      <List disablePadding>
        {conflicts.map((conflict, idx) => (
          <React.Fragment key={conflict.id}>
            {idx > 0 && <Divider component="li" />}
            <ListItem
              alignItems="flex-start"
              secondaryAction={
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => onResolve(conflict.id)}
                >
                  Keep server version
                </Button>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={operationLabel(conflict.operation)}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                    <Chip
                      label={conflict.entity}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {new Date(conflict.timestamp).toLocaleTimeString()}
                    </Typography>
                  </Stack>
                }
                secondary={conflict.serverError}
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
)

export default SyncConflictDialog
